/**
 * Environment configuration utilities
 * Handles environment-specific settings and validation
 */

// Get current environment
export const getEnvironment = () => {
  return process.env.REACT_APP_ENV || 'development';
};

// Get domain for current environment
export const getDomain = () => {
  return process.env.REACT_APP_DOMAIN || 'localhost:3000';
};

// Get the full site URL (with protocol)
export const getSiteUrl = () => {
  return process.env.REACT_APP_SITE_URL || `https://${getDomain()}`;
};

// Waitlist API endpoint (AWS Lambda Function URL or API Gateway)
export const getWaitlistApiUrl = () => {
  return process.env.REACT_APP_WAITLIST_API_URL || '';
};

// Stripe one-time-purchase configuration (mirrors the Solidly Finance buy flow)
export const getStripeConfig = () => {
  const config = {
    paymentUrl: process.env.REACT_APP_STRIPE_PAYMENT_URL,
    promoCode: process.env.REACT_APP_STRIPE_PROMO_CODE || '',
  };

  if (!config.paymentUrl) {
    console.error('REACT_APP_STRIPE_PAYMENT_URL is not configured');
    return null;
  }

  return config;
};

// Build the Stripe Payment Link URL. The after-payment redirect
// (?purchase=success back to this site) is configured on the payment link in
// the Stripe dashboard — Payment Links don't take success/cancel URL params.
// Returns null when Stripe is not configured so callers can fall back gracefully.
export const buildStripeUrl = () => {
  const config = getStripeConfig();
  if (!config) return null;

  let url = config.paymentUrl;
  if (config.promoCode) {
    const sep = url.includes('?') ? '&' : '?';
    url += `${sep}prefilled_promo_code=${encodeURIComponent(config.promoCode)}`;
  }

  return url;
};

// GA4 measurement ID (the gtag bootstrap in public/index.html consumes the same
// env var at build time; this getter exists for runtime checks/debugging).
export const getGaMeasurementId = () => {
  return process.env.REACT_APP_GA_MEASUREMENT_ID || '';
};

// Environment validation
export const validateEnvironment = () => {
  const requiredVars = ['REACT_APP_ENV', 'REACT_APP_DOMAIN', 'REACT_APP_WAITLIST_API_URL'];
  const missing = requiredVars.filter((varName) => !process.env[varName]);

  if (missing.length > 0) {
    console.error('Missing required environment variables:', missing);
    return false;
  }
  return true;
};

// Environment info for debugging
export const getEnvironmentInfo = () => {
  return {
    environment: getEnvironment(),
    domain: getDomain(),
    siteUrl: getSiteUrl(),
    waitlistConfigured: !!getWaitlistApiUrl(),
    stripeConfigured: !!getStripeConfig(),
    gaConfigured: !!getGaMeasurementId(),
    nodeEnv: process.env.NODE_ENV,
  };
};

export const logEnvironmentInfo = () => {
  const info = getEnvironmentInfo();
  console.log('Environment Info:', info);
  if (!validateEnvironment()) {
    console.warn('Some environment variables are missing. Check your .env files.');
  }
};
